import { motion } from "motion/react";

export interface GridCell {
  /** Что нарисовать в клетке. */
  content?: React.ReactNode;
  /** По клетке можно ходить. */
  playable?: boolean;
  /** Клетка выбрана игроком. */
  selected?: boolean;
  /** Подсветка последнего хода. */
  lastMove?: boolean;
  /** Подсветка выигрышной линии или взятого поля. */
  highlight?: boolean;
  /** Тёмное поле (шашки, шахматы). */
  dark?: boolean;
}

interface Props {
  size: number;
  cells: GridCell[];
  /** Разлиновка по линиям, а не по клеткам — для гомоку. */
  lined?: boolean;
  /** Перевернуть доску (я играю снизу). */
  flipped?: boolean;
  onCell(index: number): void;
}

/**
 * Универсальная сетка для шашек, реверси и гомоку.
 *
 * Своя на каждую игру не нужна: отличается только содержимое клетки, а вся
 * геометрия, подсветки и попадание пальцем — общие. Новая клеточная игра
 * подключается одним маппером «состояние → GridCell[]».
 */
export function GridBoard({ size, cells, lined, flipped, onCell }: Props) {
  // Переворот доски — чисто визуальный: индексы в модели не меняются.
  const order = flipped
    ? Array.from({ length: size * size }, (_, i) => size * size - 1 - i)
    : Array.from({ length: size * size }, (_, i) => i);

  return (
    <div
      className={`grid-board${lined ? " is-lined" : ""}`}
      style={{ gridTemplateColumns: `repeat(${size}, 1fr)` }}
    >
      {order.map((index) => {
        const cell = cells[index] ?? {};
        const cls = [
          "grid-cell",
          cell.dark ? "is-dark" : "",
          cell.playable ? "is-playable" : "",
          cell.selected ? "is-selected" : "",
          cell.lastMove ? "is-last" : "",
          cell.highlight ? "is-highlight" : "",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <div key={index} className={cls} onClick={() => onCell(index)}>
            {cell.playable && !cell.content && <span className="grid-hint" />}
            {cell.content}
          </div>
        );
      })}
    </div>
  );
}

/** Круглая фишка — реверси и гомоку. */
export function Disc({
  color,
  layoutId,
}: {
  color: "black" | "white";
  layoutId?: string;
}) {
  return (
    <motion.span
      {...(layoutId ? { layoutId } : {})}
      className={`disc disc-${color}`}
      initial={{ scale: 0.4, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 520, damping: 30 }}
    />
  );
}

/** Шашка, при необходимости с короной дамки. */
export function Checker({ side, king }: { side: 1 | 2; king: boolean }) {
  return (
    <motion.span
      className={`checker checker-${side === 1 ? "light" : "dark"}`}
      initial={{ scale: 0.6, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 520, damping: 30 }}
    >
      {king && <span className="crown">♛</span>}
    </motion.span>
  );
}
